import { useMemo } from 'react';
import { useAuthStore } from '../stores/auth.store';
import {
  parseCompatibleJwtAuthorizationClaims,
  type PermissionFlagValue,
  type ProjectVisibility,
} from '@task-tracker/shared';

type ParsedClaims = {
  is_system_admin: boolean;
  permissions: string[];
  project_visibility: ProjectVisibility;
};

function parseJwtClaims(token: string): ParsedClaims | null {
  const claims = parseCompatibleJwtAuthorizationClaims(token);
  if (!claims) return null;
  return {
    is_system_admin: claims.is_system_admin,
    permissions: claims.permissions,
    project_visibility: claims.project_visibility,
  };
}

const FALLBACK_CLAIMS: ParsedClaims = {
  is_system_admin: false,
  permissions: [],
  project_visibility: 'assigned_projects',
};

export function useJwtClaims(): ParsedClaims {
  const token = useAuthStore((s) => s.session?.access_token);
  return useMemo(() => {
    if (!token) return FALLBACK_CLAIMS;
    return parseJwtClaims(token) ?? FALLBACK_CLAIMS;
  }, [token]);
}

export function usePermission(permission: PermissionFlagValue): boolean {
  const claims = useJwtClaims();
  if (claims.is_system_admin) return true;
  return claims.permissions.includes('*') || claims.permissions.includes(permission);
}

export function useAnyPermission(permissions: PermissionFlagValue[]): boolean {
  const claims = useJwtClaims();
  if (claims.is_system_admin) return true;
  return (
    claims.permissions.includes('*') || permissions.some((p) => claims.permissions.includes(p))
  );
}

export function useIsAdmin(): boolean {
  return useJwtClaims().is_system_admin;
}

/**
 * Returns true when the current user is a limited operator who should only see
 * tasks assigned to them. Mirrors the backend `isTaskViewRestricted` predicate:
 * the role has project_visibility='assigned_tasks', or the non-admin user
 * lacks the `edit_tasks` permission (e.g. Field Operator).
 */
export function useIsRestrictedTaskView(): boolean {
  const claims = useJwtClaims();
  if (claims.is_system_admin) return false;
  if (claims.project_visibility === 'assigned_tasks') return true;
  if (claims.permissions.includes('*')) return false;
  return !claims.permissions.includes('edit_tasks');
}

/**
 * Returns true when the user's role restricts them to assigned tasks only.
 * This mode hides Dashboard, Reports, Team, and Resources surfaces.
 */
export function useIsAssignedTasksOnly(): boolean {
  const claims = useJwtClaims();
  if (claims.is_system_admin) return false;
  return claims.project_visibility === 'assigned_tasks';
}
